<template>
  <v-container fluid class="pa-6">
    <!-- Page Header -->
    <v-row justify="center" class="mb-6">
      <v-col cols="12" md="10" lg="8">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h1 class="text-h5 font-weight-bold mb-1">Quote Preview</h1>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Detailed view of the quote submission
              <template v-if="entry?.quote?.submissionId">
                &middot; <span class="font-weight-medium">{{ entry.quote.submissionId }}</span>
              </template>
              <template v-if="entry?.quote?.createdAt">
                &middot; {{ formatDate(entry.quote.createdAt) }}
              </template>
            </p>
          </div>
          <v-btn variant="outlined" prepend-icon="mdi-arrow-left" @click="goBack">
            Back to Quotes
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Content -->
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <!-- Online payment (Foxy) -->
        <v-card v-if="entry?.quote?.paymentStatus" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="success" class="mr-2" size="20">mdi-credit-card-check-outline</v-icon>
            Online Payment
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Status</th>
                  <td><PaymentStatusChip :quote="entry.quote" /></td>
                </tr>
                <tr v-if="entry.quote.checkoutMatch">
                  <th>Checkout</th>
                  <td>
                    <template v-if="entry.quote.checkoutMatch === 'unchanged'">Paid as quoted</template>
                    <template v-else-if="entry.quote.checkoutMatch === 'unknown'">
                      Not compared: the quote was saved without its cart
                    </template>
                    <template v-else>
                      <div class="text-warning font-weight-medium">
                        Changed at checkout. Go by the order, not the quote.
                      </div>
                      <ul class="checkout-changes">
                        <li v-for="(change, index) in entry.quote.checkoutChanges || []" :key="index">
                          {{ formatCheckoutChange(change) }}
                        </li>
                      </ul>
                    </template>
                  </td>
                </tr>
                <tr v-if="entry.quote.order">
                  <th>Order</th>
                  <td>
                    <router-link
                      v-if="entry.customer?.id"
                      :to="{ name: 'CustomerOrderView', params: { customerId: entry.customer.id, orderId: entry.quote.order.id } }"
                    >
                      #{{ entry.quote.order.orderNumber }}
                    </router-link>
                    <span v-else>#{{ entry.quote.order.orderNumber }}</span>
                    <span class="text-medium-emphasis">
                      ({{ entry.quote.order.source === 'foxy' ? 'paid online' : 'made by hand' }},
                      {{ entry.quote.order.orderStatus }})
                    </span>
                  </td>
                </tr>
                <tr v-if="entry.quote.amountPaid !== null">
                  <th>Amount Paid</th>
                  <td>${{ Number(entry.quote.amountPaid).toFixed(2) }}</td>
                </tr>
                <tr v-if="entry.quote.paidAt">
                  <th>Paid At</th>
                  <td>{{ formatDate(entry.quote.paidAt) }}</td>
                </tr>
                <tr v-if="entry.quote.foxyDisplayId">
                  <th>Foxy Transaction</th>
                  <td>
                    #{{ entry.quote.foxyDisplayId }}
                    <span
                      v-if="entry.quote.foxyTransactionId && entry.quote.foxyTransactionId !== entry.quote.foxyDisplayId"
                      class="text-medium-emphasis"
                    >
                      (id {{ entry.quote.foxyTransactionId }})
                    </span>
                  </td>
                </tr>
                <tr v-if="entry.quote.paymentStatus !== 'pending'">
                  <th>Test Payment</th>
                  <td>{{ entry.quote.paymentIsTest ? "Yes" : "No" }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Customer -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-account</v-icon>
            Customer Information
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="entry?.customer">
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>
                    {{ entry.customer.name }} {{ entry.customer.lastName }}
                  </td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{{ entry.customer.email }}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>{{ entry.customer.phone }}</td>
                </tr>
                <tr>
                  <th>City</th>
                  <td>{{ entry.customer.city }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Event -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-calendar-star</v-icon>
            Event Information
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="entry.event">
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Date</th>
                  <td>{{ entry.event.date }}</td>
                </tr>
                <tr>
                  <th>Time</th>
                  <td>{{ entry.event.time }}</td>
                </tr>
                <tr>
                  <th>Occasion</th>
                  <td>{{ entry.event.occasion }}</td>
                </tr>
                <tr v-if="entry.event.honoree">
                  <th>Honoree</th>
                  <td>{{ entry.event.honoree }}</td>
                </tr>
                <tr>
                  <th>Delivery</th>
                  <td>{{ entry.quote.deliveryOption?.name || entry.quote.delivery }}</td>
                </tr>
                <tr v-if="entry.event.locationType">
                  <th>Location Type</th>
                  <td>
                    {{ entry.event.locationType }}
                    <span v-if="entry.event.locationTypeOther">: {{ entry.event.locationTypeOther }}</span>
                  </td>
                </tr>
                <tr v-if="entry.event.venueName">
                  <th>Venue Name</th>
                  <td>{{ entry.event.venueName }}</td>
                </tr>
                <tr v-if="entry.event.suiteApartment">
                  <th>Suite / Apartment</th>
                  <td>{{ entry.event.suiteApartment }}</td>
                </tr>
                <tr v-if="entry.event.isOnCampPendleton">
                  <th>Camp Pendleton</th>
                  <td>
                    Yes<span v-if="entry.event.campPendletonLocation"> &middot; {{ entry.event.campPendletonLocation }}</span>
                    <span v-if="entry.event.campPendletonBuffetSetup"> &middot; Buffet setup</span>
                  </td>
                </tr>
                <tr v-if="entry.event.interestedInTablesideOrCart !== null">
                  <th>Interested in Tableside / Taco Cart</th>
                  <td>{{ yesNo(entry.event.interestedInTablesideOrCart) }}</td>
                </tr>
                <tr v-if="entry.event.complimentaryChaffers !== null">
                  <th>Complimentary Chaffers</th>
                  <td>{{ yesNo(entry.event.complimentaryChaffers) }}</td>
                </tr>
                <tr>
                  <th>Additional Message</th>
                  <td class="pre-wrap">{{ entry.event.additionalMessage }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Menu & Pricing -->
        <v-card v-if="entry?.quote" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-food-variant</v-icon>
            Menu &amp; Pricing
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Menu Name</th>
                  <td>{{ entry.quote.menuName }}</td>
                </tr>
                <tr v-if="entry.quote.menuIncludes && entry.quote.menuIncludes.trim()">
                  <th>Menu Includes</th>
                  <td>{{ entry.quote.menuIncludes }}</td>
                </tr>
                <tr>
                  <th>Selected Proteins</th>
                  <td>{{ entry.quote.selectedProteins }}</td>
                </tr>
                <tr>
                  <th>Number of Guests</th>
                  <td>{{ entry.quote.numberOfGuests }}</td>
                </tr>
                <tr v-if="entry.quote.menuPerGuestPrice !== null">
                  <th>Price per Guest</th>
                  <td>{{ money(entry.quote.menuPerGuestPrice) }}</td>
                </tr>
                <tr>
                  <th>Selected Special Offer</th>
                  <td>{{ entry.quote.selectedSpecialOffer }}</td>
                </tr>
                <tr>
                  <th>Complimentary Tasting</th>
                  <td>{{ yesNo(entry.quote.complimentaryTasting) }}</td>
                </tr>
                <tr v-if="entry.quote.complimentaryChurros !== null">
                  <th>Complimentary Churros</th>
                  <td>{{ yesNo(entry.quote.complimentaryChurros) }}</td>
                </tr>
                <tr>
                  <th>Sales Tax</th>
                  <td>{{ money(entry.quote.salesTaxTotal) }}</td>
                </tr>
                <tr>
                  <th>Total Cost</th>
                  <td class="font-weight-bold">{{ money(entry.quote.totalCost) }}</td>
                </tr>
              </tbody>
            </v-table>

            <template v-if="hasItems(entry.quote.customMenuBreakdown)">
              <h4 class="text-subtitle-2 font-weight-bold mt-4 mb-2">Custom Menu Breakdown (per guest)</h4>
              <v-table density="compact" class="text-start">
                <tbody>
                  <tr v-for="(line, i) in entry.quote.customMenuBreakdown" :key="i">
                    <td>{{ line.label }}</td>
                    <td class="text-end">{{ money(line.amount) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </v-card-text>
        </v-card>

        <!-- Protein Upgrades -->
        <v-card v-if="hasItems(entry?.quote?.selectedUpgrades)" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-arrow-up-bold-circle-outline</v-icon>
            Protein Upgrades
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <thead>
                <tr>
                  <th class="text-start">Original</th>
                  <th class="text-start">Upgraded To</th>
                  <th class="text-start">Price / Guest</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(upgrade, i) in entry.quote.selectedUpgrades" :key="i">
                  <td>{{ upgrade.originalItem?.name }}</td>
                  <td>
                    <strong>{{ upgrade.upgradedItem?.name }}</strong>
                    <div v-if="upgrade.upgradedItem?.description" class="text-caption text-medium-emphasis">
                      {{ upgrade.upgradedItem.description }}
                    </div>
                  </td>
                  <td>{{ Number(upgrade.upgradedItem?.price) > 0 ? "+" + money(upgrade.upgradedItem.price) : "Complimentary" }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Additional Upgrades (upsells) -->
        <v-card v-if="hasItems(entry?.quote?.selectedUpsells)" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-star-plus-outline</v-icon>
            Additional Upgrades
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <thead>
                <tr>
                  <th class="text-start">Upgrade</th>
                  <th class="text-start">Qty</th>
                  <th class="text-start">Unit Price</th>
                  <th class="text-start">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(upsell, i) in entry.quote.selectedUpsells" :key="upsell.upsellKey || i">
                  <td><strong>{{ upsell.title }}</strong></td>
                  <td>{{ upsell.quantity }}</td>
                  <td>
                    {{ money(upsell.unitPrice) }}
                    <span v-if="upsell.pricingMode === 'perGuest'" class="text-medium-emphasis">/ guest</span>
                  </td>
                  <td>{{ money(upsell.total) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Special offer extra portions -->
        <v-card v-if="entry?.quote?.selectedOfferUpsell" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-tag-plus-outline</v-icon>
            Special Offer Extra Portions
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr v-if="entry.quote.selectedOfferUpsell.offerTitle">
                  <th>Offer</th>
                  <td>{{ entry.quote.selectedOfferUpsell.offerTitle }}</td>
                </tr>
                <tr>
                  <th>Item</th>
                  <td>{{ entry.quote.selectedOfferUpsell.itemLabel }}</td>
                </tr>
                <tr>
                  <th>Quantity</th>
                  <td>{{ entry.quote.selectedOfferUpsell.quantity }}</td>
                </tr>
                <tr>
                  <th>Unit Price</th>
                  <td>{{ money(entry.quote.selectedOfferUpsell.unitPrice) }}</td>
                </tr>
                <tr>
                  <th>Total</th>
                  <td>{{ money(entry.quote.selectedOfferUpsell.total) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Addons -->
        <v-card class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-plus-box-multiple</v-icon>
            Selected Addons
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table
              v-if="entry?.quote?.addons && entry?.quote?.addons.length"
              density="compact"
              class="text-start"
            >
              <thead>
                <tr>
                  <th class="text-start">Addon</th>
                  <th class="text-start">Qty</th>
                  <th class="text-start">Price</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(addon, i) in entry?.quote?.addons" :key="addon.addonItemId || i">
                  <td>
                    <strong>{{ addon.name }}</strong>
                  </td>
                  <td>{{ addon.quantity }}</td>
                  <td>{{ money(addon.price) }}</td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="text-medium-emphasis">No addons selected.</div>
          </v-card-text>
        </v-card>

        <!-- Addon Package -->
        <v-card v-if="entry?.quote?.addonPackage" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="teal" class="mr-2" size="20">mdi-package-variant</v-icon>
            Addon Package
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start mb-4">
              <tbody>
                <tr>
                  <th>Package Name</th>
                  <td>{{ entry.quote.addonPackage.name }}</td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td>{{ entry.quote.addonPackage.description }}</td>
                </tr>
                <tr v-if="entry.quote.addonPackage.pricePerPerson !== null && entry.quote.addonPackage.pricePerPerson !== undefined">
                  <th>Price per Person</th>
                  <td>{{ money(entry.quote.addonPackage.pricePerPerson) }}</td>
                </tr>
                <tr>
                  <th>Package Total Price</th>
                  <td>{{ money(entry.quote.addonPackageTotalPrice) }}</td>
                </tr>
              </tbody>
            </v-table>

            <h4 class="text-subtitle-2 font-weight-bold mb-2">Package Items</h4>
            <v-table
              v-if="entry.quote.addonPackageItems && entry.quote.addonPackageItems.length"
              density="compact"
              class="text-start"
            >
              <thead>
                <tr>
                  <th class="text-start">Item</th>
                  <th class="text-start">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in entry.quote.addonPackageItems" :key="item.id">
                  <td><strong>{{ item.name }}</strong></td>
                  <td>{{ item.quantity }}</td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="text-medium-emphasis">No package items.</div>
          </v-card-text>
        </v-card>

        <!-- Selected Cart -->
        <v-card v-if="entry?.quote?.selectedCart" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="deep-orange" class="mr-2" size="20">mdi-cart</v-icon>
            Selected Cart
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-img
              v-if="entry.quote.selectedCart.image"
              :src="entry.quote.selectedCart.image"
              max-width="240"
              class="mb-4 rounded"
              cover
            ></v-img>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>{{ entry.quote.selectedCart.name }}</td>
                </tr>
                <tr v-if="entry.quote.selectedCart.description">
                  <th>Description</th>
                  <td>{{ entry.quote.selectedCart.description }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Submission & Tracking -->
        <v-card v-if="entry?.quote" class="mb-4 text-start">
          <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-information-outline</v-icon>
            Submission &amp; Tracking
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-table density="compact" class="text-start">
              <tbody>
                <tr>
                  <th>Quote ID</th>
                  <td>{{ entry.quote.id }}</td>
                </tr>
                <tr v-if="entry.quote.submissionId">
                  <th>Submission ID</th>
                  <td>{{ entry.quote.submissionId }}</td>
                </tr>
                <tr>
                  <th>Submitted</th>
                  <td>{{ formatDate(entry.quote.createdAt) }}</td>
                </tr>
                <tr>
                  <th>Channel</th>
                  <td>{{ entry.quote.channelName || entry.quote.channel }}</td>
                </tr>
                <tr v-if="entry.quote.source">
                  <th>Source</th>
                  <td>{{ entry.quote.source }}</td>
                </tr>
                <tr v-if="entry.quote.orderOnline !== null">
                  <th>Could Be Ordered Online</th>
                  <td>{{ yesNo(entry.quote.orderOnline) }}</td>
                </tr>
                <tr v-if="entry.quote.comebackOfferId">
                  <th>Comeback Offer</th>
                  <td>{{ entry.quote.comebackOfferId }}</td>
                </tr>
                <tr>
                  <th>Converted From Comeback</th>
                  <td>{{ yesNo(entry.quote.convertedFromCb) }}</td>
                </tr>
                <tr v-if="entry.quote.isComebackLinkClicked !== null">
                  <th>Comeback Link Clicked</th>
                  <td>{{ yesNo(entry.quote.isComebackLinkClicked) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          Order Created Successfully!
        </v-card-title>
        <v-card-text class="text-body-1">
          <p><strong>Order Number:</strong> {{ createdOrderNumber }}</p>
          <p>
            The order has been created from this quote and is now ready for
            management.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="showSuccessDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { quoteService, orderService } from "../services";
import { formatDate, formatCurrency, formatCheckoutChange } from "../utils";
import PaymentStatusChip from "../components/PaymentStatusChip.vue";

export default {
  name: "QuoteEntryPreviewView",
  components: { PaymentStatusChip },
  data() {
    return {
      entry: {},
      loading: true,
      creatingOrder: false,
      showSuccessDialog: false,
      createdOrderNumber: "",
    };
  },
  methods: {
    formatDate,
    formatCheckoutChange,
    money(amount) {
      if (amount === null || amount === undefined || amount === "") return "";
      return "$" + formatCurrency(amount);
    },
    yesNo(value) {
      if (value === null || value === undefined) return "";
      return value ? "Yes" : "No";
    },
    hasItems(list) {
      return Array.isArray(list) && list.length > 0;
    },
    async fetchEntry() {
      this.loading = true;
      try {
        const data = await quoteService.getQuoteEntry(this.$route.params.id);
        this.entry = { ...data };
      } finally {
        this.loading = false;
      }
    },
    async createOrderFromQuote() {
      if (
        !confirm(
          "Are you sure you want to create an order from this quote? This will copy all quote data to a new order."
        )
      ) {
        return;
      }

      this.creatingOrder = true;
      try {
        const order = await orderService.createOrder(this.entry.quote.id);
        this.createdOrderNumber = order.orderNumber;
        this.showSuccessDialog = true;
      } catch (error) {
        console.error("Error creating order:", error);
        alert("Failed to create order. Please try again.");
      } finally {
        this.creatingOrder = false;
      }
    },
    goBack() {
      this.$router.push({ name: "QuoteEntries" });
    },
  },
  mounted() {
    this.fetchEntry();
  },
};
</script>

<style scoped>
.text-start {
  text-align: left !important;
}
.checkout-changes {
  margin: 4px 0 0;
  padding-left: 18px;
}

.pre-wrap {
  white-space: pre-wrap;
}

th {
  font-weight: 600;
  white-space: nowrap;
  padding-right: 12px;
  color: #64748b;
}
</style>
